import React from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Breadcrumb,
  Space,
  Spin,
  Typography,
  message,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMisaJobLogs } from '../../hooks/useMisa';
import { misaService } from '../../services/misa.service';

const { Title } = Typography;

const JOB_LABELS: Record<string, string> = {
  'misa-invoice-sync': 'Ho\u00e1 \u0111\u01a1n \u0111i\u1ec7n t\u1eed',
  'misa-sa-order-sync': '\u0110\u01a1n b\u00e1n h\u00e0ng',
  'misa-account-object-sync': 'Kh\u00e1ch h\u00e0ng / NCC',
  'misa-customer-user-sync': '\u0110\u1ed3ng b\u1ed9 User',
  'misa-bank-account-sync': 'T\u00e0i kho\u1ea3n ng\u00e2n h\u00e0ng',
  'misa-inventory-balance-sync': 'T\u1ed3n kho',
  'misa-inventory-serial-sync': 'Serial s\u1ea3n ph\u1ea9m',
  'misa-inventory-serial-book-detail-sync': 'Serial (chi ti\u1ebft)',
  'misa-inventory-item-sync': 'Danh m\u1ee5c s\u1ea3n ph\u1ea9m',
  'misa-address-backfill': 'B\u1ed5 sung \u0111\u1ecba ch\u1ec9',
  'misa-bank-sync': 'Danh s\u00e1ch ng\u00e2n h\u00e0ng',
  'misa-stock-sync': 'Kho h\u00e0ng',
  'misa-orgunit-sync': 'Chi nh\u00e1nh',
  'misa-act-open-account-sync': 'T\u00e0i kho\u1ea3n k\u1ebf to\u00e1n',
  'misa-sync-alert': 'C\u1ea3nh b\u00e1o \u0111\u1ed3ng b\u1ed9',
};

export const MisaJobDetail: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, jobType } = useParams<{ tenantId: string; jobType: string }>();
  const { logs, loading, error, meta, page, setPage, refetch } = useMisaJobLogs(
    tenantId || '',
    jobType || ''
  );

  const jobLabel = JOB_LABELS[jobType || ''] || jobType;

  const handleTriggerSync = async () => {
    if (!tenantId || !jobType) return;
    try {
      await misaService.triggerSync(tenantId, jobType);
      message.success('Sync triggered successfully');
      refetch();
    } catch {
      message.error('Failed to trigger sync');
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'success' ? 'success' : 'error'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Records',
      dataIndex: 'recordsProcessed',
      key: 'recordsProcessed',
      width: 100,
      align: 'center',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (value) => `${value}s`,
    },
    {
      title: 'Error Message',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  // Compute stats from logs
  const lastLog = logs[0];
  const totalRecords = logs.reduce((sum, l) => sum + l.recordsProcessed, 0);
  const avgDuration = logs.length
    ? Math.round(logs.reduce((sum, l) => sum + l.duration, 0) / logs.length)
    : 0;
  const errorCount = logs.filter((l) => l.status === 'failed').length;

  if (loading && logs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Lỗi tải dữ liệu"
        description={error.message || 'Vui lòng thử lại sau'}
        type="error"
        showIcon
        action={<Button onClick={refetch}>Thử lại</Button>}
      />
    );
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/misa">MISA</Link> },
          { title: jobLabel },
        ]}
      />

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/misa')}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {jobLabel}
            </Title>
            <Tag>{tenantId}</Tag>
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleTriggerSync}
          >
            Manual Sync
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Last Run"
              value={lastLog ? dayjs(lastLog.timestamp).format('HH:mm DD/MM') : 'N/A'}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Records Processed"
              value={totalRecords}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Duration"
              value={avgDuration}
              suffix="s"
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Error Count"
              value={errorCount}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: errorCount > 0 ? '#f5222d' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Sync History">
        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (p) => setPage(p),
          }}
        />
      </Card>
    </div>
  );
};

export default MisaJobDetail;
